import { BASE_COMPONENT, WizBaseComponent } from './../../../core/wiz-base-component';
import { Component, ChangeDetectionStrategy, forwardRef, Input, ContentChildren, QueryList, ElementRef, ViewChild, ViewContainerRef,
  OnDestroy, AfterContentInit } from '@angular/core';
import { WizRadioButton } from '../wiz-radio-button/wiz-radio-button';
import { WizTranslatableComponent } from '../../../core/wiz-translatable-component';
import { WizValidationErrors, WizValidatorFn, WizValidators } from './../../../../services/validators/wiz-validators';
import { IWIZ_COMPONENT} from '../../../../interfaces/iwiz-component';
import { VALUE_DOWN, VALUE_LEFT, VALUE_RIGHT, VALUE_UP, WIZ_ERROR_MESSAGE, WIZ_EVENT_TYPE, WIZ_LATERAL, WizMathUtils } from '@wizeoo/akm-core-lib';
import { WizEvent } from '../../../../events/wiz-event';
import { WizEventManager } from '../../../../events/wiz-event.manager';

/**
 * Composant groupe de boutons radio.
 * Conteneur des boutons du groupe
 * Gère la valeur sélectionnée pour le groupe
 */
@Component({
  selector: 'wiz-radio-group',
  templateUrl: './wiz-radio-group.html',
  styleUrls: ['./wiz-radio-group.scss'],
  providers: [
    { provide: BASE_COMPONENT, useExisting: forwardRef(() => WizRadioGroup) },
    { provide: WizBaseComponent, useExisting: forwardRef(() => WizRadioGroup) },
    { provide: IWIZ_COMPONENT, useExisting: forwardRef(() => WizRadioGroup) }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WizRadioGroup extends WizTranslatableComponent implements AfterContentInit, OnDestroy {

  /** Enfants radio buttons */
  @ContentChildren(forwardRef(() => WizRadioButton), { descendants: true }) radioButtonChildren: QueryList<WizRadioButton>;
  // Conteneur principal
  @ViewChild('radioGroupHead', { static: true, read: ElementRef }) protected radioGroupHead: ElementRef;
  /** container en bas pour injection message d'erreur */
  @ViewChild('errorContainer', { read: ViewContainerRef, static: true }) protected errorContainer: ViewContainerRef;

  /** Classe css pour skin "disabled" */
  public disabledClassName: string = 'disabled-class';

  /** Bouton ayant actuellement le focus */
  private _focusedButton: WizRadioButton;

  /**
   * Liste des boutons radio du groupe
   */
  private _buttons: Array<WizRadioButton> = new Array<WizRadioButton>();

  /** Nom du groupe, permet le regroupement des inputs radio */
  private _wizName: string;
  public get wizName(): string {
    return this.getWizName();
  }
  @Input()
  public set wizName(pName: string) {
    this.setWizName(pName);
  }
  protected getWizName(): string {
    // Nom de groupe par défaut si non renseigné
    if (!this._wizName) {
      this._wizName = WizMathUtils.createUUID();
    }
    return this._wizName;
  }
  protected setWizName(pValue): void {
    this._wizName = pValue;
  }

  /**
   * @override Pour mise à jour des boutons
   * @param value Nouvelle valeur sélectionnée pour le groupe
   */
  public setWizValue(value: string): void {
    this._wizValue = value;
    this._buttons.forEach(radio => {
      radio.refresh();
    });
  }

  /**
   * Validation saisie obligatoire
   * @param errorMessage Clé message d'erreur
   */
  protected getRequiredValidator(errorMessage: string): WizValidatorFn {
    return WizValidators.wizRequired(errorMessage);
  }

  /** Clé de message d'erreur custom */
  private _wizRequiredErrorMessage: string;
  @Input() public set wizRequiredErrorMessage(str: string) {
    this.setWizRequiredErrorMessage(str);
  }
  public get wizRequiredErrorMessage(): string {
    return this.getWizRequiredErrorMessage();
  }
  protected setWizRequiredErrorMessage(str: string): void {
    this._wizRequiredErrorMessage = str;
    this.cdref.markForCheck();
  }
  public getWizRequiredErrorMessage(): string {
    return this._wizRequiredErrorMessage;
  }

  /** le composant a à minima une erreur */
  protected _hasErrors: boolean = false;
  public get hasErrors(): boolean {
    return this._hasErrors;
  }
  /** Affichage d'erreur sous les boutons */
  private _wizShowErrors: boolean = false;
  // attention, le composant doit porter la directive WizValidator pour être pris en compte
  @Input()
  public set wizShowErrors(v: boolean) {
    this.setWizShowError(v);
  }
  public get wizShowErrors(): boolean {
    return this.getWizShowError();
  }
  protected setWizShowError(v: boolean): void {
    this._wizShowErrors = v;
    this.cdref.markForCheck();
  }
  protected getWizShowError(): boolean {
    return this._wizShowErrors;
  }

  /** Position du libellé des boutons (before/after), constante WIZ_LATERAL */
  @Input() public set wizLabelPosition(position :string) {
    this.setWizLabelPosition(position);
  }
  public get wizLabelPosition(): string {
    return this.getWizLabelPosition();
  }
  protected setWizLabelPosition(position: string): void {
    this._wizLabelPosition = position;
    this._buttons.forEach(radio => {
      radio.refresh();
    });
    this.cdref.markForCheck();
  }
  public getWizLabelPosition(): string {
    return this._wizLabelPosition;
  }
  private _wizLabelPosition: string = WIZ_LATERAL.AFTER;

  /**
   * Référencement du groupe aux boutons
   */
  public ngAfterContentInit(): void {
    this.radioButtonChildren.forEach((btnRadio => {
      this.addButton(btnRadio);
    }));
    WizEventManager.addListener(this, this.elementRef, 'keydown', (e: KeyboardEvent) => this.onKeyDown(e));
  }

  /** Suppression de l'écouteur pour saisie clavier */
  public ngOnDestroy() {
    WizEventManager.removeListener(this, this.elementRef, 'keydown');
  }
  /**
   * Ecouteur sur key down pour navigation clavier entre les boutons par les flèches
   * @param event Evènement key down
   */
  private onKeyDown(event: KeyboardEvent): void {
    // ouverture sur enter / space
    if (event.key === VALUE_UP || event.key === VALUE_DOWN
        || event.key === VALUE_LEFT || event.key === VALUE_RIGHT) {
      event.stopPropagation();
      event.preventDefault();
      const focusedButton:WizRadioButton = this._focusedButton;
      const selectNext: boolean = (event.key === VALUE_DOWN || event.key === VALUE_RIGHT);
      let radioButtonToFocus: WizRadioButton;
      const buttonArray: WizRadioButton[] = this.radioButtonChildren.toArray();
      if (focusedButton && buttonArray.length > 1) {
        let currentIndex: number = buttonArray.indexOf(focusedButton);
        let lengthChecked: number = 0;
        while (!radioButtonToFocus && lengthChecked <= buttonArray.length) {
          if (selectNext) {
            if (currentIndex === buttonArray.length-1) {
              currentIndex = 0;
            } else {
              currentIndex++;
            }
          } else {
            if (currentIndex === 0) {
              currentIndex = buttonArray.length-1;
            } else {
              currentIndex--;
            }
          }
          radioButtonToFocus = buttonArray[currentIndex];
          if (radioButtonToFocus && radioButtonToFocus.wizDisabled) {
            radioButtonToFocus = null;
          }
          lengthChecked++;
        }
      }
      if (radioButtonToFocus) {
        radioButtonToFocus.wizFocus();
      }
    }
  }

  /**
   * Ajout d'un bouton radio dans le groupe
   * @param button
   */
  public addButton(button: WizRadioButton): void {
    if (button) {
      button.wizGroup = this;
      this._buttons.push(button);
    }
  }

  /**
   * Changement de valeur depuis les boutons
   * @param value Nouvelle valeur sélectionnée pour le groupe
   */
  public changeButtonValue(value: string): void {
    this.wizValue = value;
    // dispatch d'event
    const evt: WizEvent = new WizEvent(WIZ_EVENT_TYPE.CHANGE, this, this._wizValue);
    WizEventManager.dispatchEvent(this.elementRef, evt);
    this._hasErrors = false; // suppression de l'erreur
    this.manageErrorDisplay();// gestion du rendu (suppression du marquage physique de l'erreur)
  }

  /** suppression / affichage des marqueurs d'erreur & messages */
  protected manageErrorDisplay(): void {
    if (this._hasErrors) {
    }
    else {
      if (this.getErrorVCRef()) {// clear container
        this.getErrorVCRef().clear();
      }
    }
    this.cdref.markForCheck();
    this._buttons.forEach(radio => {
      radio.refresh();
    });
  }

  /**
   * Appel externe sur focusOut depuis la directive wizValidator
   * @param pErrors la collection d'erreur
   * @param wizShowErrors : //TODO JRO demande externe ? sert à rien ici
   */
  public onWizValidation(pErrors: WizValidationErrors, wizShowErrors: boolean): void {
    this._hasErrors = pErrors.length > 0;
    this.manageErrorDisplay();
  }

  /**
   * Error message container : contrat d'interface pour la directive wizValidator
   * @return ViewContainerRef
   */
  public getErrorVCRef(): ViewContainerRef {
    return this.errorContainer;
  }

  /**
   * contrat d'interface pour la directive wizValidator
   * @return WizValidatorFn[]
   */
  public getValidators(): WizValidatorFn[] {

    const validators: WizValidatorFn[] = []; // tableau de fonctions de validation
    if (this.wizMandatory) {
      const errorMessage = this.wizRequiredErrorMessage ? this.wizRequiredErrorMessage : WIZ_ERROR_MESSAGE.REQUIRED;
      validators.push(this.getRequiredValidator(errorMessage));
    }
    return validators;
  }

  public wizGetFocusFromButton(pFocusedButton: WizRadioButton): void {
    this._focusedButton = pFocusedButton;
    this.wizGetFocus();
  }

  /**
   * @override pour suppression du message d'erreur (required)
   */
  public wizGetFocus(): void {
    super.wizGetFocus();
    this._hasErrors = false; // suppression de l'erreur
    this.manageErrorDisplay();// gestion du rendu (suppression du marquage physique de l'erreur)
  }

  /**
   * @override pour gestion de la tabulation
   */
  public wizLoseFocus(): void {
    super.wizLoseFocus();
    setTimeout(() => {
      // rétablissement du tabindex après focus out
      this.renderer.setAttribute(this.radioGroupHead.nativeElement, 'tabindex', '0');
    }, 0);
  }

  /** application du style effectif (disabled, autres...) */
  public checkStyle() {
    // déclenché par des méthodes internes (wizDisabled...)
    if (this.wizDisabled) { // inactif
      this.renderer.addClass(this.elementRef.nativeElement, this.disabledClassName);
      this.renderer.setAttribute(this.radioGroupHead.nativeElement, 'tabindex', '-1');
    } else { // actif
      this.renderer.removeClass(this.elementRef.nativeElement, this.disabledClassName);
      this.renderer.setAttribute(this.radioGroupHead.nativeElement, 'tabindex', '0');
    }
  }

  /**
   * Handler de focus
   * @param e Evenement focus
   */
  public onFocus(e:FocusEvent): void {
    // Focus au bouton séléctionné ou le 1er bouton sélectionnable
    let radioButtonToFocus: WizRadioButton;
    const buttonArray: WizRadioButton[] = this.radioButtonChildren.toArray();
    for (let i = 0, len = buttonArray.length; i < len; i++) {
      const btnRadio: WizRadioButton = buttonArray[i];
      if (!btnRadio.wizDisabled) {
        if (this.wizValue && btnRadio.wizValue === this.wizValue) {
          radioButtonToFocus = btnRadio;
          break;
        }
        if (!radioButtonToFocus) {
          radioButtonToFocus = btnRadio;
          if (!this.wizValue) {
            break;
          }
        }
      }
    }
    if (radioButtonToFocus){
      radioButtonToFocus.wizFocus();
      // Suppression du tabindex pour ne pas reprendre le focus après un enfant (ctrl-tab)
      this.renderer.setAttribute(this.radioGroupHead.nativeElement, 'tabindex', '-1');
    }
  }
}
